
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { UserRole } from '@/hooks/auth/types';
import { toast } from '@/components/ui/sonner';
import AccessDenied from './AccessDenied';
import AuthLoadingScreen from '@/components/auth/AuthLoadingScreen';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole | UserRole[];
  fallbackPath?: string;
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  fallbackPath = "/dashboard"
}) => {
  const { session, profile, isLoading, hasAccess } = useSupabaseAuth();
  const location = useLocation();
  const [accessChecked, setAccessChecked] = useState(false);
  
  useEffect(() => {
    if (!isLoading) {
      setAccessChecked(true);
    }
  }, [isLoading]);

  // Show loading state with timeout
  if (isLoading) {
    return <AuthLoadingScreen message="Checking your access permissions..." showSkeleton={true} />;
  }

  // Redirect to login if not authenticated
  if (!session) {
    console.log("No session detected in RoleProtectedRoute, redirecting to login");
    
    toast.error("Please log in to access this content", {
      duration: 4000,
    });
    
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check for role-based access - handle both single role and array of roles
  const allowedRolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  const hasRequiredRole = allowedRolesArray.some(role => hasAccess(role));
  
  // If doesn't have required role, show access denied
  if (!hasRequiredRole) {
    console.log("User doesn't have required role(s):", allowedRoles);
    
    return <AccessDenied returnPath={fallbackPath} />;
  }

  // User has the required role
  return <>{children}</>;
};

export default RoleProtectedRoute;
